import { Button } from "../../ui/button";
import { Connect } from "../../Authentication/Connect";
import { useMemo, useState } from "react";
import { useUser } from "@/hooks/useUserProvider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { Link2Icon, Loader2Icon, TrashIcon } from "lucide-react";
import { Separator } from "../../ui/separator";
import { Label } from "../../ui/label";
import { UserSocial } from "@/lib/types";
import { Alert, AlertDescription } from "../../ui/alert";
import { request } from "@/lib/request";
import { IntegrationIcon } from "@/lib/icons";
import { googleScopeLabels } from "@/lib/integrations";
import { Bitrix24ManageAccessDialog } from "./Bitrix24ManageAccessDialog";

export const Manage = ({ service }: { service: string }) => {
  const { accessToken, user, syncUser } = useUser();
  const [integrationToDelete, setIntegrationToDelete] =
    useState<UserSocial | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = useMemo(() => {
    switch (service) {
      case "apple":
        return "Manage Apple Integration";
      case "github":
        return "Manage Github Integration";
      case "github_personal":
        return "Manage Github Personal Integration";
      case "gitlab":
        return "Manage Gitlab Integration";
      case "google":
        return "Manage Google Integration";
      case "trello":
        return "Manage Trello Integration";
      case "toggl":
        return "Manage Toggl Integration";
      case "atlassian":
        return "Manage Atlassian Integration";
      case "pinterest":
        return "Manage Pinterest Integration";
      case "linear":
        return "Manage Linear Integration";
      case "bitrix24":
        return "Manage Bitrix24 Integration";
      case "slack":
        return "Manage Slack Integration";
    }
  }, [service]);

  const serviceName = useMemo(() => {
    return service.charAt(0).toUpperCase() + service.slice(1);
  }, [service]);

  const handleDelete = async () => {
    if (!accessToken || !integrationToDelete) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await request
        .delete(`/user/social/${integrationToDelete.id}`)
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Failed to delete integration.",
          );
        });

      await syncUser();
      setIntegrationToDelete(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {!user.socials[service]?.length ? (
        <Connect
          service={service}
          size="default"
          title={`Connect to ${service.replace(/[_\s-]/g, " ").replace(/\b\w/g, l => l.toUpperCase())}`}
        />
      ) : (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="secondary"
              size="default"
              className="flex w-[140px] items-center text-primary/90"
            >
              <IntegrationIcon
                integration={service}
                defaultIcon={Link2Icon}
                size="16px"
              />
              <span className="-mr-1 font-bold text-primary">
                {user.socials[service].length}
              </span>
              {` account${user.socials[service].length > 1 ? "s" : ""}`}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>
                {`You are connected to ${serviceName} with the following accounts`}
              </DialogDescription>
            </DialogHeader>
            <Separator />
            {user.socials[service].map(social => (
              <div
                key={social.id}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setIntegrationToDelete(social);
                      setError(null);
                    }}
                  >
                    <TrashIcon />
                  </Button>
                  <div className="flex flex-col gap-1">
                    <Label>{social.email}</Label>
                    {service === "google" &&
                    Array.isArray(social.scopes) &&
                    social.scopes.length ? (
                      <span className="text-muted-foreground text-xs">
                        {`Allowed: ${social.scopes
                          .map(scope => googleScopeLabels[scope] ?? scope)
                          .join(", ")}`}
                      </span>
                    ) : null}
                  </div>
                </div>
                {service === "bitrix24" ? (
                  <Bitrix24ManageAccessDialog social={social} />
                ) : null}
              </div>
            ))}
            <div className="flex justify-end">
              {service === "bitrix24" ? (
                <div className="flex items-center gap-2">
                  <Connect
                    service={service}
                    size="default"
                    title={`Add other ${serviceName} accounts`}
                  />
                </div>
              ) : (
                <Connect
                  service={service}
                  size="default"
                  title={`Add other ${serviceName} accounts`}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
      <Dialog
        open={integrationToDelete !== null}
        onOpenChange={() => setIntegrationToDelete(null)}
      >
        {integrationToDelete ? (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Are you absolutely sure?</DialogTitle>
              <DialogDescription>
                This will disconnect {integrationToDelete.email} from{" "}
                {serviceName}, you can reconnect at any time but widgets that
                rely on this account will stop working.
              </DialogDescription>
            </DialogHeader>
            {error ? (
              <Alert className="text-red-500">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <DialogFooter>
              <Button
                variant="default"
                onClick={() => setIntegrationToDelete(null)}
                size="sm"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2Icon className="me-2 animate-spin" />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="me-2 size-4" />
                    Delete
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
};
